import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Menu, Camera, X, Heart, Mic, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";

import PieceCard from "../components/PieceCard";
import PieceDetailModal from "../components/modals/PieceDetailModal";
import { PieceGridSkeleton } from "../components/ui/Skeleton";
import { useToast } from "../components/ui/Toast";
import { useFavorites } from "../hooks/useFavorites";
import { useCart } from "../hooks/useCart";
import { useRecentlyViewed } from "../hooks/useRecentlyViewed";
import { useAnalytics } from "../hooks/useAnalytics";
import { useVoiceSearch } from "../hooks/useVoiceSearch";
import PhotoSearchModal from "../components/modals/PhotoSearchModal";
import KmRadiusModal from "../components/modals/KmRadiusModal";
import { supabase } from "../lib/supabase";

/* ── Haversine: distancia en km entre dos puntos ── */
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/* ── Leer/guardar ubicación en localStorage ── */
function readLocStorage() {
  try { return JSON.parse(localStorage.getItem("yonkers_loc") || "null"); }
  catch { return null; }
}
function writeLocStorage(v) {
  try { localStorage.setItem("yonkers_loc", JSON.stringify(v)); } catch {}
}

const API = import.meta.env.VITE_API_URL || "/api";

/* ── Categorías ── */
const CATEGORIES = [
  { id: "all",        label: "Todos"      },
  { id: "motores",    label: "Motores"    },
  { id: "carroceria", label: "Carrocería" },
  { id: "suspension", label: "Suspensión" },
  { id: "frenos",     label: "Frenos"     },
  { id: "electrico",  label: "Eléctrico"  },
];

const CONDITIONS = ["Todos", "Usado", "Buen estado", "Como nuevo", "Nuevo"];

/* ── Alturas fijas ── */
const HEADER_H   = 48;
const SEARCH_H   = 42;
const CHIPS_H    = 32;
const TOP_AREA_H = HEADER_H + SEARCH_H + CHIPS_H + 28;

const EMPTY_FILTERS = {
  condition: "",
  minPrice:  "",
  maxPrice:  "",
  yearFrom:  "",
  yearTo:    "",
};

const SUPER_ADMIN_EMAILS = [
  "josuetb19997@gmail.com",
  "josuetaborab@gmail.com",
  "josuetabora2012@gmail.com",
];

export default function Home({ user, openLogin }) {
  const navigate   = useNavigate();
  const { track }  = useAnalytics();
  const [selectedPiece,  setSelectedPiece]  = useState(null);
  const [selectedId,     setSelectedId]     = useState(null);
  const [pieces,         setPieces]         = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [query,          setQuery]          = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [showFilters,    setShowFilters]    = useState(false);
  const [filters,        setFilters]        = useState(EMPTY_FILTERS);
  const [pendingFilters, setPendingFilters] = useState(EMPTY_FILTERS);

  const { toast } = useToast();
  const { items: recentItems, addPiece: addRecent } = useRecentlyViewed();
  const { favorites, toggleFavorite, isFavorite } = useFavorites();
  const { addToCart, isInCart } = useCart();
  const [showFavs,       setShowFavs]       = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showMenu,       setShowMenu]       = useState(false);
  const [showUserMenu,   setShowUserMenu]   = useState(false);
  const [isAutolote,     setIsAutolote]     = useState(false);
  const [voiceError,     setVoiceError]     = useState("");
  const userMenuRef = useRef(null);

  /* ── Filtro de ubicación ── */
  const [locFilter,    setLocFilter]    = useState(() => readLocStorage());
  const [showLocModal, setShowLocModal] = useState(false);

  function applyLocation(loc, radius) {
    const next = loc ? { name: loc.name, lat: loc.lat, lng: loc.lng, radius } : null;
    setLocFilter(next);
    writeLocStorage(next);
  }

  function clearLocation() {
    setLocFilter(null);
    writeLocStorage(null);
  }

  const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(user?.email ?? "");

  /* Verificar si es dueño de autolote aprobado */
  useEffect(() => {
    if (!user || isSuperAdmin) return;
    supabase
      .from("autolote_profiles")
      .select("status")
      .eq("owner_id", user.id)
      .maybeSingle()
      .then(({ data }) => { if (data?.status === "approved") setIsAutolote(true); });
  }, [user]);

  /* Cerrar el menú de usuario al hacer click fuera */
  useEffect(() => {
    if (!showUserMenu) return;
    function handleOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [showUserMenu]);

  async function handleLogout() {
    await supabase.auth.signOut();
    setShowUserMenu(false);
  }

  /* ── Voz ── */
  const { listening, supported: voiceSupported, startListening, stopListening } = useVoiceSearch({
    onResult: (text) => {
      setQuery(text);
      setVoiceError("");
    },
    onError: (msg) => setVoiceError(msg),
  });

  /* ── Cuántos filtros activos ── */
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  /* ── Ocultar bottom nav con modal abierto ── */
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("toggleBottomNav", { detail: { hidden: !!selectedPiece } })
    );
  }, [selectedPiece]);

  /* ── Fetch (Supabase directo) ── */
  const fetchPieces = useCallback(async (searchText = "", category = "all", f = filters) => {
    try {
      setLoading(true);
      let q = supabase.from("pieces").select("*");

      // Busqueda de texto
      if (searchText.trim()) {
        const t = searchText.trim();
        q = q.or(`title.ilike.%${t}%,brand.ilike.%${t}%,years.ilike.%${t}%,yonker.ilike.%${t}%,city.ilike.%${t}%`);
      }

      // Categoria
      if (category && category !== "all") {
        q = q.ilike("title", `%${category}%`);
      }

      // Condicion
      if (f.condition && f.condition !== "Todos") {
        q = q.ilike("condition", `%${f.condition}%`);
      }

      // Precio
      if (f.minPrice) q = q.gte("price", Number(f.minPrice));
      if (f.maxPrice) q = q.lte("price", Number(f.maxPrice));

      q = q.order("created_at", { ascending: false }).limit(200);

      const { data, error } = await q;
      if (error) throw error;

      // Filtro de año (client-side ya que years es texto)
      let result = Array.isArray(data) ? data : [];
      if (f.yearFrom || f.yearTo) {
        result = result.filter((p) => {
          const y = parseInt((p.years || "").replace(/\D/g, ""), 10);
          if (isNaN(y)) return true;
          if (f.yearFrom && y < Number(f.yearFrom)) return false;
          if (f.yearTo   && y > Number(f.yearTo))   return false;
          return true;
        });
      }

      setPieces(result);
    } catch (err) {
      console.error("❌ Error:", err);
      setPieces([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchPieces("", "all", filters); }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchPieces(query, activeCategory, filters);
      // 📊 Rastrear búsquedas con texto
      if (query.trim().length > 2) {
        track("search", "home", { query: query.trim() });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, activeCategory, filters]);

  const handleOpenCard = useCallback((p) => {
    setSelectedPiece(p);
    setSelectedId(p.id);
    addRecent(p);
  }, [addRecent]);

  /* ── Heart: favorito + carrito ── */
  function handleToggleFavorite(piece) {
    toggleFavorite(piece.id);
    if (!isFavorite(piece.id)) {
      // Se está agregando → también va al carrito
      addToCart({
        id:      piece.id,
        type:    "piece",
        title:   piece.title,
        brand:   piece.brand,
        years:   piece.years,
        price:   piece.price,
        images:  piece.images,
        whatsapp:piece.whatsapp,
        yonker:  piece.yonker,
        city:    piece.city,
      });
      toast.info(`🛒 "${piece.title || "Pieza"}" agregada al carrito`);
    }
  }

  /* ── Aplicar filtros ── */
  function applyFilters() {
    setFilters(pendingFilters);
    setShowFilters(false);
  }

  function clearFilters() {
    setPendingFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setShowFilters(false);
  }

  /* ── Piezas a mostrar (normal o favoritas, con filtro de ubicación) ── */
  const displayedPieces = useMemo(() => {
    let list = showFavs ? pieces.filter((p) => isFavorite(p.id)) : pieces;

    if (locFilter && locFilter.lat && locFilter.radius && locFilter.radius < 99999) {
      list = list.filter((p) => {
        const lat = Number(p.lat);
        const lng = Number(p.lng);
        // Si la pieza no tiene GPS, la incluimos igual
        if (!lat || !lng || (lat === 0 && lng === 0)) return true;
        return haversine(locFilter.lat, locFilter.lng, lat, lng) <= locFilter.radius;
      });
    }
    return list;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pieces, showFavs, favorites, locFilter]);

  return (
    <div style={st.page}>
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
      `}</style>

      {/* ══════════════════════════════
          TOP AREA — fijo
      ══════════════════════════════ */}
      <div style={st.topArea}>

        {/* ── Tira azul: menú · logo · avatar + buscador ── */}
        <div style={st.headerStrip}>

          <header style={st.header}>
            <Menu
              size={22}
              color="#fff"
              style={{ position: "absolute", left: 18, cursor: "pointer" }}
              onClick={() => setShowMenu(true)}
            />

            <img
              src="/logo-yonkers.png"
              alt="Yonkers"
              style={st.logo}
            />

            {!user ? (
              <button onClick={openLogin} style={st.loginBtn} type="button">
                Login
              </button>
            ) : (
              <div ref={userMenuRef} style={{ position: "absolute", right: 14, zIndex: 200 }}>
                {/* Avatar botón */}
                <button
                  onClick={() => setShowUserMenu((v) => !v)}
                  style={{ ...st.avatar, position: "relative", cursor: "pointer", border: "none" }}
                  aria-label="Menú de usuario"
                >
                  {user.user_metadata?.avatar_url ? (
                    <img
                      src={user.user_metadata.avatar_url}
                      alt=""
                      style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    user.email?.charAt(0).toUpperCase()
                  )}
                </button>

                {/* Dropdown */}
                {showUserMenu && (
                  <div style={st.userDropdown}>
                    {/* Info usuario */}
                    <div style={st.dropdownHeader}>
                      <div style={st.dropdownAvatar}>
                        {user.email?.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={st.dropdownName}>
                          {user.user_metadata?.full_name || user.user_metadata?.name || "Mi cuenta"}
                        </div>
                        <div style={st.dropdownEmail}>{user.email}</div>
                      </div>
                    </div>

                    <div style={st.dropdownDivider} />

                    {/* Opciones según rol */}
                    {isSuperAdmin && (
                      <button style={st.dropdownItem} onClick={() => { navigate("/admin"); setShowUserMenu(false); }}>
                        <span style={st.dropdownItemIcon}>🛡️</span>
                        Panel Admin
                      </button>
                    )}
                    {(isSuperAdmin || isAutolote) && (
                      <button style={st.dropdownItem} onClick={() => { navigate("/my-pieces"); setShowUserMenu(false); }}>
                        <span style={st.dropdownItemIcon}>🚗</span>
                        Mi autolote
                      </button>
                    )}

                    <div style={st.dropdownDivider} />

                    <button style={{ ...st.dropdownItem, color: "#ef4444" }} onClick={handleLogout}>
                      <span style={st.dropdownItemIcon}>🚪</span>
                      Cerrar sesión
                    </button>
                  </div>
                )}
              </div>
            )}
          </header>

          {/* ── Buscador ── */}
          <div style={st.searchRow}>
            <div style={st.searchBar}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                stroke="#facc15" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>

              <input
                placeholder="Buscar pieza"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={st.input}
                autoComplete="off"
                spellCheck={false}
              />

              {query.length > 0 && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  style={st.clearBtn}
                  aria-label="Limpiar"
                >
                  <X size={14} color="rgba(26,45,90,0.5)" strokeWidth={2.5} />
                </button>
              )}

              <span style={st.country}>HN</span>
            </div>

            {/* Botón voz */}
            {voiceSupported && (
              <button
                style={{
                  ...st.camBtn,
                  background: listening ? "#ef4444" : "#1e4b8f",
                  transition: "background 0.2s",
                }}
                type="button"
                aria-label={listening ? "Detener voz" : "Buscar por voz"}
                onClick={listening ? stopListening : startListening}
              >
                <Mic size={16} color="#fff" strokeWidth={1.8} />
              </button>
            )}

            {/* Botón cámara */}
            <button
              style={st.camBtn}
              type="button"
              aria-label="Buscar por foto"
              onClick={() => setShowPhotoModal(true)}
            >
              <Camera size={16} color="#fff" strokeWidth={1.8} />
            </button>
          </div>

        </div>{/* fin headerStrip */}

        {/* ── Tira blanca: chips de categoría + favoritos + ubicación ── */}
        <div style={st.chipsStrip}>
          <div style={st.chipsRow}>
            {/* ── Chip de ubicación ── */}
            <button
              key="loc"
              type="button"
              style={{
                ...st.chip,
                ...(locFilter ? st.chipOn : st.chipOff),
                display: "flex", alignItems: "center", gap: 4,
                whiteSpace: "nowrap", flexShrink: 0,
              }}
              onClick={() => setShowLocModal(true)}
            >
              <MapPin size={10} style={{ display: "inline" }} />
              {locFilter
                ? `${locFilter.name} · ${locFilter.radius >= 99999 ? "Todo HN" : `${locFilter.radius} km`}`
                : "Ubicación"}
              {locFilter && (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Limpiar ubicación"
                  style={{ marginLeft: 2, lineHeight: 1 }}
                  onClick={(e) => { e.stopPropagation(); clearLocation(); }}
                  onKeyDown={(e) => e.key === "Enter" && clearLocation()}
                >
                  <X size={10} />
                </span>
              )}
            </button>

            {/* Separador visual */}
            <div style={{ width: 1, height: 18, background: "#e5e7eb", flexShrink: 0 }} />

            {/* Chip favoritos */}
            <button
              key="favs"
              type="button"
              style={{
                ...st.chip,
                ...(showFavs ? st.chipFav : st.chipOff),
              }}
              onClick={() => setShowFavs(!showFavs)}
            >
              <Heart size={11} style={{ display: "inline", marginRight: 4 }}
                fill={showFavs ? "#1e4b8f" : "none"}
                stroke={showFavs ? "#1e4b8f" : "#6b7280"}
              />
              Favoritos {favorites.length > 0 && `(${favorites.length})`}
            </button>

            {CATEGORIES.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                style={{
                  ...st.chip,
                  ...(activeCategory === id && !showFavs ? st.chipOn : st.chipOff),
                }}
                onClick={() => { setActiveCategory(id); setShowFavs(false); }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

      </div>

      {/* ══════════════════════════════
          CONTENIDO
      ══════════════════════════════ */}
      <div style={{ paddingTop: TOP_AREA_H }}>

        {/* Banner favoritos vacíos */}
        {showFavs && favorites.length === 0 && (
          <div style={st.favBanner}>
            <Heart size={32} color="#facc15" />
            <p style={{ margin: "8px 0 4px", fontWeight: 700, color: "#1e4b8f" }}>
              Aún no tienes favoritos
            </p>
            <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>
              Toca el corazón en cualquier pieza para guardarla aquí
            </p>
          </div>
        )}

        {/* ── Visto recientemente ── */}
        {recentItems.length > 0 && !showFavs && !query && activeCategory === "all" && (
          <div style={st.recentWrap}>
            <div style={st.recentHeader}>
              <span style={st.recentTitle}>👁 Visto recientemente</span>
            </div>
            <div style={st.recentScroll}>
              {recentItems.map((p) => {
                const img = p.images?.length ? p.images[0] : null;
                const price = p.price ? `L ${Number(p.price).toLocaleString("es-HN")}` : null;
                return (
                  <div
                    key={p.id}
                    style={{ ...st.recentCard, width: 100, flexShrink: 0,
                      background: "#fff", border: "1px solid #e5e7eb",
                      boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}
                    onClick={() => handleOpenCard(p)}
                  >
                    {img
                      ? <img src={img} alt={p.title} style={st.recentImg} />
                      : <div style={{ ...st.recentNoImg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🔩</div>
                    }
                    <div style={st.recentLabel}>{p.title}</div>
                    <div style={st.recentSub}>{price || p.city || p.brand || ""}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={st.list}>
          {loading ? (
            <PieceGridSkeleton count={6} />
          ) : displayedPieces.length === 0 && !showFavs ? (
            <div style={st.empty}>
              <span style={{ fontSize: 36 }}>🔍</span>
              <p style={{ margin: "8px 0 0", fontWeight: 600, color: "#374151" }}>Sin resultados</p>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: "#9ca3af" }}>Intenta con otro término o categoría</p>
            </div>
          ) : (
            displayedPieces.map((piece) => (
              <PieceCard
                key={piece.id}
                piece={piece}
                selected={selectedId === piece.id}
                onOpen={handleOpenCard}
                onSelect={() => setSelectedId(piece.id)}
                isFavorite={isFavorite(piece.id)}
                onToggleFavorite={() => handleToggleFavorite(piece)}
                onSellerClick={(ownerId) => navigate(`/seller/${ownerId}`)}
              />
            ))
          )}
        </div>
      </div>

      {/* ══════════════════════════════
          MODAL PIEZA
      ══════════════════════════════ */}
      <PieceDetailModal
        piece={selectedPiece}
        onClose={() => setSelectedPiece(null)}
        isFavorite={selectedPiece ? isFavorite(selectedPiece.id) : false}
        onToggleFavorite={selectedPiece ? () => handleToggleFavorite(selectedPiece) : null}
      />


      {/* ══════════════════════════════
          MENÚ LATERAL
      ══════════════════════════════ */}
      {showMenu && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 10020,
            display: "flex",
          }}
          onClick={() => setShowMenu(false)}
        >
          <div
            style={{
              width: 260,
              height: "100%",
              background: "#fff",
              padding: "0 0 32px",
              display: "flex",
              flexDirection: "column",
              boxShadow: "4px 0 24px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del menú */}
            <div style={{ background: "linear-gradient(180deg,#1e4b8f,#0f3e82)", padding: "40px 20px 24px" }}>
              <img src="/logo-yonkers.png" alt="Yonkers" style={{ height: 60, objectFit: "contain" }} />
              {user && (
                <div style={{ marginTop: 12, color: "#fff", fontSize: 13, opacity: 0.8 }}>
                  {user.email}
                </div>
              )}
            </div>
            {/* Items del menú */}
            <div style={{ padding: "16px 0", flex: 1 }}>
              {[
                { label: "🏠  Inicio", action: () => { setShowMenu(false); } },
                { label: "🔍  Buscar pieza", action: () => { setShowMenu(false); document.querySelector("input")?.focus(); } },
                { label: "📋  Mis piezas", action: () => { setShowMenu(false); navigate("/my-pieces"); } },
                { label: "💬  Yonky", action: () => { setShowMenu(false); navigate("/messages"); } },
                { label: "📣  Solicitar pieza", action: () => { setShowMenu(false); navigate("/request"); } },
                ...(isSuperAdmin ? [
                  { label: "📊  Estadísticas", action: () => { setShowMenu(false); navigate("/stats"); } },
                ] : []),
              ].map(({ label, action }) => (
                <button
                  key={label}
                  type="button"
                  onClick={action}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "14px 24px",
                    background: "none",
                    border: "none",
                    fontSize: 15,
                    fontWeight: 600,
                    color: "#1a2d5a",
                    cursor: "pointer",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* Footer del menú */}
            {!user && (
              <button
                type="button"
                onClick={() => { setShowMenu(false); openLogin(); }}
                style={{
                  margin: "0 20px",
                  padding: "13px 0",
                  borderRadius: 12,
                  background: "#facc15",
                  border: "none",
                  fontWeight: 700,
                  fontSize: 15,
                  color: "#1a2d5a",
                  cursor: "pointer",
                }}
              >
                Iniciar sesión
              </button>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════
          MODAL FOTO
      ══════════════════════════════ */}
      <PhotoSearchModal
        open={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        onSubmit={(q) => { setQuery(q); setShowPhotoModal(false); }}
      />

      {/* ══════════════════════════════
          MODAL UBICACIÓN
      ══════════════════════════════ */}
      <KmRadiusModal
        open={showLocModal}
        onClose={() => setShowLocModal(false)}
        location={locFilter ? { name: locFilter.name, lat: locFilter.lat, lng: locFilter.lng } : null}
        radius={locFilter?.radius ?? 50}
        onApply={applyLocation}
      />

      {/* ══ VOICE LISTENING INDICATOR ══ */}
      {listening && (
        <div style={{
          position: "fixed",
          bottom: 90,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#ef4444",
          color: "#fff",
          padding: "10px 22px",
          borderRadius: 40,
          fontSize: 13,
          fontWeight: 700,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          gap: 8,
          boxShadow: "0 4px 20px rgba(239,68,68,0.4)",
          whiteSpace: "nowrap",
        }}>
          🎙️ Escuchando… habla ahora
        </div>
      )}

      {/* ══ VOICE ERROR TOAST ══ */}
      {voiceError && (
        <div style={{
          position: "fixed",
          bottom: 90,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#111827",
          color: "#fff",
          padding: "10px 20px",
          borderRadius: 40,
          fontSize: 13,
          fontWeight: 600,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          gap: 8,
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          whiteSpace: "nowrap",
          cursor: "pointer",
        }}
          onClick={() => setVoiceError("")}
        >
          ⚠️ {voiceError}
        </div>
      )}

      {/* ══════════════════════════════
          PANEL DE FILTROS
      ══════════════════════════════ */}
      {showFilters && (
        <div style={st.filterOverlay} onClick={() => setShowFilters(false)}>
          <div style={st.filterPanel} onClick={(e) => e.stopPropagation()}>
            <div style={st.filterHeader}>
              <span style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>Filtros</span>
              <button style={st.filterClose} onClick={() => setShowFilters(false)}>
                <X size={18} color="#6b7280" />
              </button>
            </div>

            {/* Condición */}
            <div style={st.filterGroup}>
              <label style={st.filterLabel}>Condición</label>
              <div style={st.conditionRow}>
                {CONDITIONS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    style={{
                      ...st.conditionBtn,
                      ...(pendingFilters.condition === (c === "Todos" ? "" : c)
                        ? st.conditionBtnOn
                        : st.conditionBtnOff),
                    }}
                    onClick={() =>
                      setPendingFilters((f) => ({
                        ...f,
                        condition: c === "Todos" ? "" : c,
                      }))
                    }
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Rango de precio */}
            <div style={st.filterGroup}>
              <label style={st.filterLabel}>Precio (L)</label>
              <div style={st.rangeRow}>
                <input
                  type="number"
                  placeholder="Mínimo"
                  value={pendingFilters.minPrice}
                  onChange={(e) => setPendingFilters((f) => ({ ...f, minPrice: e.target.value }))}
                  style={st.rangeInput}
                />
                <span style={{ color: "#9ca3af", fontSize: 14 }}>—</span>
                <input
                  type="number"
                  placeholder="Máximo"
                  value={pendingFilters.maxPrice}
                  onChange={(e) => setPendingFilters((f) => ({ ...f, maxPrice: e.target.value }))}
                  style={st.rangeInput}
                />
              </div>
            </div>

            {/* Rango de año */}
            <div style={st.filterGroup}>
              <label style={st.filterLabel}>Año del vehículo</label>
              <div style={st.rangeRow}>
                <input
                  type="number"
                  placeholder="Desde"
                  value={pendingFilters.yearFrom}
                  onChange={(e) => setPendingFilters((f) => ({ ...f, yearFrom: e.target.value }))}
                  style={st.rangeInput}
                  min="1960"
                  max="2026"
                />
                <span style={{ color: "#9ca3af", fontSize: 14 }}>—</span>
                <input
                  type="number"
                  placeholder="Hasta"
                  value={pendingFilters.yearTo}
                  onChange={(e) => setPendingFilters((f) => ({ ...f, yearTo: e.target.value }))}
                  style={st.rangeInput}
                  min="1960"
                  max="2026"
                />
              </div>
            </div>

            {/* Botones */}
            <div style={st.filterActions}>
              <button style={st.clearBtn2} onClick={clearFilters}>
                Limpiar filtros
              </button>
              <button style={st.applyBtn} onClick={applyFilters}>
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────
   STYLES
───────────────────────────────────── */
const st = {
  page: {
    minHeight: "100vh",
    width: "100%",
    background: "#f3f4f6",
  },

  topArea: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    background: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.09)",
  },

  // Tira azul que contiene el logo
  headerStrip: {
    background: "#1e4b8f",
    paddingBottom: 8,
  },

  header: {
    height: HEADER_H,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },

  logo: {
    height: 72,
    objectFit: "contain",
    filter: "drop-shadow(0 1px 3px rgba(0,0,0,.2))",
    pointerEvents: "none",
  },

  loginBtn: {
    position: "absolute",
    right: 14,
    background: "#facc15",
    border: "none",
    padding: "5px 12px",
    borderRadius: 20,
    fontWeight: 700,
    fontSize: 12,
    cursor: "pointer",
    color: "#111",
  },

  avatar: {
    position: "relative",
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "#facc15",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 13,
    color: "#111",
    flexShrink: 0,
    overflow: "hidden",
  },

  userDropdown: {
    position: "absolute",
    top: "calc(100% + 10px)",
    right: 0,
    width: 220,
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 8px 30px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.06)",
    overflow: "hidden",
    animation: "fadeIn 0.15s ease",
  },
  dropdownHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "14px 14px 12px",
    background: "#f8fafc",
  },
  dropdownAvatar: {
    width: 36, height: 36,
    borderRadius: "50%",
    background: "#1e3a8a",
    color: "#facc15",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 800, fontSize: 14, flexShrink: 0,
  },
  dropdownName: {
    fontSize: 13, fontWeight: 700, color: "#0f172a",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  dropdownEmail: {
    fontSize: 11, color: "#94a3b8",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  dropdownDivider: {
    height: 1, background: "#f1f5f9",
  },
  dropdownItem: {
    display: "flex", alignItems: "center", gap: 10,
    width: "100%", padding: "11px 14px",
    background: "none", border: "none",
    fontSize: 13, fontWeight: 600, color: "#374151",
    cursor: "pointer", textAlign: "left",
    transition: "background 0.1s",
    fontFamily: "system-ui, sans-serif",
  },
  dropdownItemIcon: {
    fontSize: 16, flexShrink: 0,
  },

  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "0 10px",
    marginBottom: 6,
  },

  searchBar: {
    flex: 1,
    height: SEARCH_H,
    background: "#fff",
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    padding: "0 14px",
    gap: 8,
    border: "2px solid #facc15",
  },

  input: {
    flex: 1,
    border: "none",
    background: "transparent",
    outline: "none",
    fontSize: 14,
    fontWeight: 500,
    color: "#1a2d5a",
    minWidth: 0,
  },

  clearBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    padding: "0 2px",
    flexShrink: 0,
  },

  country: {
    fontWeight: 800,
    fontSize: 11,
    color: "rgba(26,45,90,0.4)",
    letterSpacing: "0.05em",
    flexShrink: 0,
  },

  camBtn: {
    width: SEARCH_H,
    height: SEARCH_H,
    borderRadius: 999,
    border: "none",
    background: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },

  filterBadge: {
    position: "absolute",
    top: -4,
    right: -4,
    background: "#facc15",
    color: "#111",
    borderRadius: "50%",
    width: 16,
    height: 16,
    fontSize: 10,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  // Tira blanca para los chips
  chipsStrip: {
    background: "#fff",
    borderBottom: "1px solid #f0f0f0",
  },

  chipsRow: {
    display: "flex",
    gap: 0,
    padding: "0 8px",
    overflowX: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },

  chip: {
    flexShrink: 0,
    height: CHIPS_H,
    padding: "0 12px",
    borderRadius: 0,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
    borderBottom: "2.5px solid transparent",
    whiteSpace: "nowrap",
    transition: "all .15s",
    WebkitTapHighlightColor: "transparent",
    display: "flex",
    alignItems: "center",
    background: "transparent",
  },

  chipOn: {
    color: "#1e4b8f",
    borderBottomColor: "#facc15",
    fontWeight: 700,
    background: "transparent",
  },

  chipOff: {
    color: "#6b7280",
    background: "transparent",
  },

  chipFav: {
    color: "#1e4b8f",
    borderBottomColor: "#facc15",
    fontWeight: 700,
    background: "transparent",
  },

  list: {
    padding: "14px 14px 120px",
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 14,
  },

  empty: {
    gridColumn: "1 / -1",
    textAlign: "center",
    padding: 40,
    color: "#9ca3af",
    fontSize: 14,
  },

  favBanner: {
    textAlign: "center",
    padding: "32px 24px 0",
  },

  /* ── Filter panel ── */
  filterOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 10000,
    display: "flex",
    alignItems: "flex-end",
  },

  filterPanel: {
    width: "100%",
    background: "#fff",
    borderRadius: "20px 20px 0 0",
    padding: "20px 20px 36px",
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },

  filterHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  filterClose: {
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "flex",
    padding: 4,
  },

  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  filterLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
  },

  conditionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },

  conditionBtn: {
    padding: "6px 14px",
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    border: "1.5px solid transparent",
    transition: "all .15s",
  },

  conditionBtnOn: {
    background: "#1e4b8f",
    color: "#fff",
    borderColor: "#1e4b8f",
  },

  conditionBtnOff: {
    background: "#f3f4f6",
    color: "#374151",
    borderColor: "#e5e7eb",
  },

  rangeRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  rangeInput: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 10,
    border: "1.5px solid #e5e7eb",
    fontSize: 14,
    outline: "none",
    color: "#111827",
  },

  filterActions: {
    display: "flex",
    gap: 10,
    marginTop: 4,
  },

  clearBtn2: {
    flex: 1,
    padding: 13,
    borderRadius: 12,
    border: "1.5px solid #e5e7eb",
    background: "#fff",
    fontSize: 14,
    cursor: "pointer",
  },

  applyBtn2: {
    flex: 2,
    padding: 13,
    borderRadius: 12,
    border: "none",
    background: "linear-gradient(135deg,#1e4b8f,#0f3e82)",
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },

  recentWrap: { padding: "14px 14px 4px" },
  recentHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  recentTitle: { fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em" },
  recentScroll: {
    display: "flex", gap: 10, overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none", msOverflowStyle: "none",
    paddingBottom: 4,
  },
  recentCard: {
    borderRadius: 12, padding: 0, cursor: "pointer", overflow: "hidden",
    display: "flex", flexDirection: "column", textAlign: "left",
    transition: "box-shadow 0.15s",
  },
  recentImg: { width: "100%", height: 66, objectFit: "cover", display: "block" },
  recentNoImg: { width: "100%", height: 66, background: "#f3f4f6" },
  recentLabel: { fontSize: 11, fontWeight: 600, color: "#111827", padding: "5px 6px 1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  recentSub:   { fontSize: 10, color: "#9ca3af", padding: "0 6px 5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
};
